import React from 'react';
import {
  AdminWebsiteLeadDto,
  exportAdminWebsiteLeadsCsv,
  getAdminWebsiteLeads,
  importAdminWebsiteLeads,
  PageDto,
  useApiClient,
  WebsiteLeadImportResultDto,
} from '../api/client';
import type { WebsiteLeadFilterQuery } from '../api/client';
import { EmptyRow, FilterBar, PageHeader, PageShell, PaginationBar, StatusBadge } from '../components/ui';
import { useToast } from '../toast/ToastProvider';
import { formatDateTime } from '../utils/dateFormat';

const PAGE_SIZE = 20;
const USER_MATCH_OPTIONS = [
  { value: '', label: 'All matches' },
  { value: 'REGISTERED', label: 'Registered' },
  { value: 'NOT_REGISTERED', label: 'Not registered' },
  { value: 'REGISTERED_DELETED', label: 'Registered deleted' },
];

function formatOptional(value?: string | null) {
  return value?.trim() || '-';
}

function formatMatch(lead: AdminWebsiteLeadDto) {
  if (!lead.registered) {
    return null;
  }
  return lead.registeredUserName || lead.registeredUserEmail || lead.registeredUserId || 'Registered user';
}

export const LeadsPage: React.FC = () => {
  const { request, authFetch } = useApiClient();
  const { showError, showSuccess } = useToast();
  const [page, setPage] = React.useState<PageDto<AdminWebsiteLeadDto> | null>(null);
  const [q, setQ] = React.useState('');
  const [draftQ, setDraftQ] = React.useState('');
  const [postalCode, setPostalCode] = React.useState('');
  const [draftPostalCode, setDraftPostalCode] = React.useState('');
  const [userMatch, setUserMatch] = React.useState('');
  const [draftUserMatch, setDraftUserMatch] = React.useState('');
  const [pageNumber, setPageNumber] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [importResult, setImportResult] = React.useState<WebsiteLeadImportResultDto | null>(null);

  const leadFilters = React.useMemo<WebsiteLeadFilterQuery>(() => ({
    q,
    postalCode,
    userMatch,
  }), [postalCode, q, userMatch]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setPage(await getAdminWebsiteLeads(request, { page: pageNumber, size: PAGE_SIZE, ...leadFilters }));
    } catch (err) {
      showError((err as Error)?.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [leadFilters, pageNumber, request, showError]);

  React.useEffect(() => {
    load();
  }, [load]);

  function submitSearch(event: React.FormEvent) {
    event.preventDefault();
    setQ(draftQ);
    setPostalCode(draftPostalCode);
    setUserMatch(draftUserMatch);
    setPageNumber(0);
  }

  async function uploadCsv(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedFile) {
      showError('Select a CSV file first.');
      return;
    }
    setUploading(true);
    try {
      const result = await importAdminWebsiteLeads(request, selectedFile);
      setImportResult(result);
      setSelectedFile(null);
      showSuccess(`CSV imported: ${result.created} created, ${result.updated} updated, ${result.skipped} skipped.`);
      setPageNumber(0);
      await load();
    } catch (err) {
      showError((err as Error)?.message || 'CSV import failed');
    } finally {
      setUploading(false);
    }
  }

  async function exportCsv() {
    setExporting(true);
    try {
      const exportFile = await exportAdminWebsiteLeadsCsv(authFetch, leadFilters);
      const url = window.URL.createObjectURL(exportFile.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportFile.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showError((err as Error)?.message || 'Failed to export leads');
    } finally {
      setExporting(false);
    }
  }

  const leads = page?.content || [];

  return (
    <PageShell>
      <PageHeader
        title="Leads"
        description="Website signups captured from the public form. Email is unique, repeated signups update the existing lead, and the table shows whether the email already exists as an app user."
      />

      <FilterBar className="grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]">
        <form onSubmit={submitSearch} className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_8rem_12rem_auto_auto_auto]">
          <label className="min-w-0">
            <span className="sr-only">Search leads</span>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Search name, mail, phone"
              value={draftQ}
              onChange={(event) => setDraftQ(event.target.value)}
            />
          </label>
          <label>
            <span className="sr-only">PLZ</span>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              inputMode="numeric"
              placeholder="PLZ"
              value={draftPostalCode}
              onChange={(event) => setDraftPostalCode(event.target.value)}
            />
          </label>
          <label>
            <span className="sr-only">User match</span>
            <select
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={draftUserMatch}
              onChange={(event) => setDraftUserMatch(event.target.value)}
            >
              {USER_MATCH_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <button className="btn btn-primary" type="submit" disabled={loading}>Search</button>
          {(q || draftQ || postalCode || draftPostalCode || userMatch || draftUserMatch) && (
            <button
              className="btn btn-neutral"
              type="button"
              onClick={() => {
                setQ('');
                setDraftQ('');
                setPostalCode('');
                setDraftPostalCode('');
                setUserMatch('');
                setDraftUserMatch('');
                setPageNumber(0);
              }}
            >
              Clear
            </button>
          )}
          <button className="btn btn-neutral whitespace-nowrap" type="button" disabled={exporting} onClick={exportCsv}>
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </form>

        <form onSubmit={uploadCsv} className="flex flex-col gap-2 sm:flex-row">
          <input
            aria-label="CSV file"
            className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            type="file"
            accept=".csv,text/csv"
            onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
          />
          <button className="btn btn-neutral" type="submit" disabled={uploading || !selectedFile}>
            {uploading ? 'Uploading...' : 'Upload CSV'}
          </button>
        </form>
      </FilterBar>

      {importResult && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <div className="font-semibold">
            Imported {importResult.processed} rows: {importResult.created} created, {importResult.updated} updated, {importResult.skipped} skipped.
          </div>
          {importResult.errors.length > 0 && (
            <div className="mt-2 max-h-24 overflow-auto text-xs">
              {importResult.errors.map((error) => (
                <div key={error}>{error}</div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5">
        <div className="flex items-center justify-between border-b px-4 py-3 text-sm text-gray-600">
          <span>{page?.totalElements ?? 0} leads found</span>
          {loading && <span>Loading...</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 font-semibold">Timestamp</th>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Mail</th>
                <th className="px-4 py-3 font-semibold">Message</th>
                <th className="px-4 py-3 font-semibold">PLZ</th>
                <th className="px-4 py-3 font-semibold">Phone</th>
                <th className="px-4 py-3 font-semibold">User match</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <LeadRow key={lead.id} lead={lead} />
              ))}
              {!loading && leads.length === 0 && <EmptyRow colSpan={7}>No leads found.</EmptyRow>}
            </tbody>
          </table>
        </div>
        <PaginationBar
          loading={loading}
          pageNumber={page?.number ?? pageNumber}
          pageSize={page?.size ?? PAGE_SIZE}
          rowCount={leads.length}
          totalElements={page?.totalElements}
          isLast={page?.last ?? true}
          previousLabel="Previous"
          nextLabel="Next"
          onPrevious={() => setPageNumber((value) => Math.max(0, value - 1))}
          onNext={() => setPageNumber((value) => value + 1)}
        />
      </div>
    </PageShell>
  );
};

function LeadRow({ lead }: { lead: AdminWebsiteLeadDto }) {
  const match = formatMatch(lead);
  return (
    <tr className="border-t align-top">
      <td className="whitespace-nowrap px-4 py-3 text-gray-600">{formatDateTime(lead.createdAt)}</td>
      <td className="px-4 py-3 font-semibold text-gray-900">{formatOptional(lead.name)}</td>
      <td className="px-4 py-3">
        <a className="font-medium text-secondary hover:underline" href={`mailto:${lead.email}`}>{lead.email}</a>
      </td>
      <td className="max-w-md px-4 py-3 text-gray-700">
        <div className="line-clamp-3 whitespace-pre-wrap break-words">{formatOptional(lead.message)}</div>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-gray-700">{formatOptional(lead.postalCode)}</td>
      <td className="whitespace-nowrap px-4 py-3 text-gray-700">{formatOptional(lead.phone)}</td>
      <td className="px-4 py-3">
        {lead.registered ? (
          <div className="grid gap-1">
            <StatusBadge tone={lead.registeredUserDeleted ? 'amber' : 'green'}>
              {lead.registeredUserDeleted ? 'Registered deleted' : 'Registered'}
            </StatusBadge>
            <div className="text-xs text-gray-600">{match}</div>
          </div>
        ) : (
          <StatusBadge tone="slate">Not registered</StatusBadge>
        )}
      </td>
    </tr>
  );
}

export default LeadsPage;
